import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateEquipment, clearError } from '../redux/slices/equipmentsSlice';
import { toast } from 'react-toastify';
import { LoadingModal } from './LoadingModal';
import { LocationAutocomplete } from './LocationAutocomplete';

const UpdateEquipmentModal = ({ setUpdateModal, viewEquipment }) => {
  const dispatch = useDispatch();

  const [name, setName] = useState('');
  const [qty, setQty] = useState('');
  const [rentalPrice, setRentalPrice] = useState('');
  const [location, setLocation] = useState('');
  const [category, setCategory] = useState('');
  const [images, setImages] = useState([]);
  const [imageInput, setImageInput] = useState('');
  const [specs, setSpecs] = useState('');
  const [description, setDescription] = useState('');
  const [tutorials, setTutorials] = useState('');
  const [selfTransport, setSelfTransport] = useState('');
  const [translations, setTranslations] = useState({
    en: { name: '', category: '', description: '' },
    es: { name: '', category: '', description: '' },
  });
  const isLoading = useSelector((state) => state.equipments.loading);
  const error = useSelector((state) => state.equipments.error);

  // Populate form with current equipment data
  useEffect(() => {
    console.log('Current Equipment:', viewEquipment);
    if (viewEquipment) {
      setName(viewEquipment.name || '');
      setQty((viewEquipment.totalInstances || viewEquipment.qty || 0).toString());
      setRentalPrice(viewEquipment.rentalPrice || '');
      setLocation(viewEquipment.location || '');
      setCategory(viewEquipment.category || '');
      setImages(viewEquipment.images || []);
      setSpecs(viewEquipment.specs || '');
      setDescription(viewEquipment.description || '');
      setTutorials(viewEquipment.tutorials || '');
      setSelfTransport(viewEquipment.selfTransport || '');

      // Populate translations if they exist, otherwise use current values
      setTranslations({
        en: {
          name: viewEquipment.translations?.en?.name || viewEquipment.name || '',
          category: viewEquipment.translations?.en?.category || viewEquipment.category || '',
          description:
            viewEquipment.translations?.en?.description || viewEquipment.description || '',
        },
        es: {
          name: viewEquipment.translations?.es?.name || '',
          category: viewEquipment.translations?.es?.category || '',
          description: viewEquipment.translations?.es?.description || '',
        },
      });
    }
  }, [viewEquipment]);

  const handleAddImage = () => {
    if (imageInput.trim()) {
      setImages((prevImages) => [...prevImages, imageInput.trim()]);
      setImageInput('');
    }
  };

  const handleRemoveImage = (index) => {
    setImages((prevImages) => prevImages.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const parsedRentalPrice = parseFloat(rentalPrice);
    const parsedQty = parseInt(qty, 10);

    if (isNaN(parsedRentalPrice)) {
      alert('Please enter valid number for rental price.');
      return;
    }

    if (isNaN(parsedQty) || parsedQty < 0) {
      alert('Please enter a valid quantity (must be a number >= 0).');
      return;
    }

    try {
      console.log('this is current equip ', viewEquipment._id);
      await dispatch(
        updateEquipment({
          id: viewEquipment._id,
          name,
          qty: parsedQty,
          rentalPrice: parsedRentalPrice,
          location,
          category,
          images,
          specs,
          description,
          tutorials,
          selfTransport,
          translations,
        })
      ).unwrap();

      toast.success('Equipment updated successfully!');
      setUpdateModal(false);
    } catch (error) {
      toast.error('Failed to update equipment: ' + error);
    }
  };

  const handleDismissError = () => {
    dispatch(clearError());
    setUpdateModal(false);
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
      {(isLoading || error) && (
        <LoadingModal
          message="Updating equipment..."
          error={error}
          onClose={error ? handleDismissError : null}
        />
      )}
      <div className="bg-white p-6 rounded-lg w-1/3 h-3/4 overflow-hidden flex flex-col">
        <h3 className="text-lg font-bold mb-4">Update Equipment</h3>
        <div className="overflow-y-auto flex-grow pr-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name (English) */}
            <div>
              <label className="block font-semibold" htmlFor="translations-en-name">
                Name (English)
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                id="translations-en-name"
                value={translations.en.name}
                onChange={(e) => {
                  setTranslations({
                    ...translations,
                    en: { ...translations.en, name: e.target.value },
                  });
                  setName(e.target.value);
                }}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            {/* Name (Spanish) */}
            <div>
              <label className="block font-semibold" htmlFor="translations-es-name">
                Name (Spanish)
              </label>
              <input
                type="text"
                id="translations-es-name"
                value={translations.es.name}
                onChange={(e) =>
                  setTranslations({
                    ...translations,
                    es: { ...translations.es, name: e.target.value },
                  })
                }
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
            {/* Quantity - Editable */}
            <div>
              <label className="block font-semibold" htmlFor="qty">
                Quantity
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                id="qty"
                min="0"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Changing quantity will automatically create or delete equipment instances. Reserved
                instances cannot be deleted.
              </p>
            </div>
            <div>
              <label className="block font-semibold" htmlFor="rentalPrice">
                Rental Price
              </label>
              <input
                type="number"
                id="rentalPrice"
                value={rentalPrice}
                onChange={(e) => setRentalPrice(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <LocationAutocomplete
                id="location"
                name="location"
                label="Location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                required={true}
              />
            </div>
            {/* Category (English) */}
            <div>
              <label className="block font-semibold" htmlFor="translations-en-category">
                Category (English)
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                id="translations-en-category"
                value={translations.en.category}
                onChange={(e) => {
                  setTranslations({
                    ...translations,
                    en: { ...translations.en, category: e.target.value },
                  });
                  setCategory(e.target.value);
                }}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            {/* Category (Spanish) */}
            <div>
              <label className="block font-semibold" htmlFor="translations-es-category">
                Category (Spanish)
              </label>
              <input
                type="text"
                id="translations-es-category"
                value={translations.es.category}
                onChange={(e) =>
                  setTranslations({
                    ...translations,
                    es: { ...translations.es, category: e.target.value },
                  })
                }
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block font-semibold" htmlFor="images">
                Image URLs
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  id="imageInput"
                  value={imageInput}
                  onChange={(e) => setImageInput(e.target.value)}
                  className="flex-grow p-2 border border-gray-300 rounded-md"
                  placeholder="Enter image URL"
                />
                <button
                  type="button"
                  onClick={handleAddImage}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold px-4 rounded-md"
                >
                  Add
                </button>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {images.map((image, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <img
                      src={image}
                      alt={`Preview ${index}`}
                      className="w-16 h-16 object-cover rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="text-red-500 text-xs mt-1"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="block font-semibold" htmlFor="tutorials">
                Self-Transport Requirements
              </label>
              <textarea
                id="selfTransport"
                value={selfTransport}
                onChange={(e) => setSelfTransport(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows="4"
                placeholder="If user selects they do not need pickup or delivery assistance. E.g. Requires heavy-duty trailer, 2-ton towing capacity, flatbed with ramps, tie-down straps, etc."
                required
              />
            </div>
            <div>
              <label className="block font-semibold" htmlFor="specs">
                Specifications
              </label>
              <textarea
                id="specs"
                value={specs}
                onChange={(e) => setSpecs(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows="2"
                required
              />
            </div>
            {/* Description (English) */}
            <div>
              <label className="block font-semibold" htmlFor="translations-en-description">
                Description (English)
                <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                id="translations-en-description"
                value={translations.en.description}
                onChange={(e) => {
                  setTranslations({
                    ...translations,
                    en: { ...translations.en, description: e.target.value },
                  });
                  setDescription(e.target.value);
                }}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows="3"
                required
              />
            </div>

            {/* Description (Spanish) */}
            <div>
              <label className="block font-semibold" htmlFor="translations-es-description">
                Description (Spanish)
              </label>
              <textarea
                id="translations-es-description"
                value={translations.es.description}
                onChange={(e) =>
                  setTranslations({
                    ...translations,
                    es: { ...translations.es, description: e.target.value },
                  })
                }
                className="w-full p-2 border border-gray-300 rounded-md"
                rows="3"
              />
            </div>
            <div>
              <label className="block font-semibold" htmlFor="tutorials">
                Tutorials
              </label>
              <textarea
                id="tutorials"
                value={tutorials}
                onChange={(e) => setTutorials(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows="2"
                required
              />
            </div>
          </form>
        </div>
        <div className="flex justify-between mt-4">
          <button
            type="button"
            onClick={() => setUpdateModal(false)}
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md"
            disabled={isLoading}
          >
            Update Equipment
          </button>
        </div>
      </div>
    </div>
  );
};

export { UpdateEquipmentModal };
